import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { LoggingService } from '../services/logging.service';

/**
 * Set on a request's `HttpContext` to suppress the error.http log entry for a
 * 404 response on that request — for lookups where a 404 is an expected,
 * routine outcome (e.g. cchsid not found) rather than an operational fault.
 * Any other status is logged as usual even when this is set.
 * @ticket EB-4153
 */
export const SKIP_HTTP_ERROR_LOG_FOR_404 = new HttpContextToken<boolean>(() => false);

/**
 * Captures failed HTTP responses as operational log events and re-throws them
 * unchanged so downstream `catchError`/subscribers still handle the error.
 *
 * @ticket EB-3798
 * Status → level mapping: `>=500` → ERROR (server fault); `>=400 && <500` →
 * WARN (client/recoverable); `0` → WARN (network/timeout/CORS, transient class).
 * The response body is deliberately NOT logged (PII risk); only status, the URL
 * PATH (query string stripped — it can carry user input like `?search=<name>`),
 * method and the request-bound `correlationId` (omitted when the header is
 * absent) are emitted. Field keys are bare (no `_` prefix) for uniformity with
 * the other emit sites — the bridge owns GELF custom-field prefixing. Must be
 * wired LAST in `withInterceptors` so the correlation-id header is already
 * present on the request it inspects.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const loggingService = inject(LoggingService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const skip404 = err.status === 404 && req.context.get(SKIP_HTTP_ERROR_LOG_FOR_404);
        if (!skip404) {
          const fields = {
            httpStatus: err.status,
            requestedUrl: req.url.split('?')[0],
            method: req.method,
            correlationId: req.headers.get('correlation-id') ?? undefined,
          };

          if (err.status >= 500) {
            loggingService.error('error.http', fields);
          } else {
            loggingService.warn('error.http', fields);
          }
        }
      }

      return throwError(() => err);
    }),
  );
};
